import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import calmMusic from "../assets/sound/MP_2.mp3";

import re1 from "../assets/video/RE_1.mp4";
import re2 from "../assets/video/RE_2.mp4";
import re3 from "../assets/video/RE_3.mp4";
import re4 from "../assets/video/RE_4.mp4";
import re5 from "../assets/video/RE_5.mp4";
import re6 from "../assets/video/RE_6.mp4";
import re7 from "../assets/video/RE_7.mp4";
import re8 from "../assets/video/RE_8.mp4";
import re9 from "../assets/video/RE_9.mp4";
import re10 from "../assets/video/RE_10.mp4";

const LIGHT_API = "http://127.0.0.1:18790";

const REPORT_ROUTE = "/result"; // ✅ 실제 리포트 라우트로 수정 가능

// 4-2-6-2 호흡
const PHASES = [
    { key: "inhale", labelKey: "들이마시기", ms: 4000, scale: 1.25 },
    { key: "hold1", labelKey: "멈춤", ms: 2000, scale: 1.25 },
    { key: "exhale", labelKey: "내쉬기", ms: 6000, scale: 0.85 },
    { key: "hold2", labelKey: "멈춤", ms: 2000, scale: 0.85 }
];

const DURATION_MS = 60_000; // 1분
const TICK_MS = 100;

export default function StressRelief() {
    const { t } = useTranslation();

    const nav = useNavigate();
    const { state } = useLocation(); // ✅ 리포트에서 넘어온 state 그대로 유지

    const musicRef = useRef(null);
    const videoRef = useRef(null);

    const [phaseIdx, setPhaseIdx] = useState(0);
    const [running, setRunning] = useState(true);

    // 진행바
    const [elapsedMs, setElapsedMs] = useState(0);

    // ✅ 완료 토스트 + 페이드 전환
    const [showDoneToast, setShowDoneToast] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);

    const phase = PHASES[phaseIdx];

    const startReliefLight = async () => {
        try {
            await axios.post(`${LIGHT_API}/light/relax/start`, {
                inhale_ms: 4000,
                hold_ms: 2000,
                exhale_ms: 6000,
                hold2_ms: 2000,
                base_hue: 33000,
                sat: 140,
                v_min: 40,
                v_max: 180
            });
        } catch {}
    };

    const stopReliefLight = async () => {
        try {
            await axios.post(`${LIGHT_API}/light/relief/stop`, {}, { timeout: 800 });
        } catch {}
    };

    const videoSrc = useMemo(() => {
        const list = [re1, re2, re3, re4, re5, re6, re7, re8, re9, re10];
        return list[Math.floor(Math.random() * list.length)];
    }, []);

    const playMedia = async () => {
        try {
            if (videoRef.current) {
                videoRef.current.muted = true;
                await videoRef.current.play();
            }
        } catch {}

        try {
            if (musicRef.current) {
                musicRef.current.currentTime = 0;
                await musicRef.current.play();
            }
        } catch {}
    };

    // 진입 시 시작
    useEffect(() => {
        startReliefLight();
        playMedia();

        return () => {
            stopReliefLight();
            try {
                if (musicRef.current) musicRef.current.pause();
                if (videoRef.current) videoRef.current.pause();
            } catch {}
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 호흡 시퀀스
    useEffect(() => {
        if (!running) return;

        const tmr = setTimeout(() => {
            setPhaseIdx(i => (i + 1) % PHASES.length);
        }, phase.ms);

        return () => clearTimeout(tmr);
    }, [phaseIdx, running, phase.ms]);

    const finishAndReturn = async () => {
        if (!running) return;
        setRunning(false);

        // ✅ 1) 완료 토스트 1초
        setShowDoneToast(true);

        await new Promise(r => setTimeout(r, 1000));

        // ✅ 3) 페이드아웃 시작
        setFadeOut(true);

        // ✅ 4) 페이드 타임(450ms) 후 정리 + 이동
        await new Promise(r => setTimeout(r, 450));

        try {
            if (musicRef.current) musicRef.current.pause();
            if (videoRef.current) videoRef.current.pause();
        } catch {}
        await stopReliefLight();

        nav(REPORT_ROUTE, {
            state: { ...state, reliefDone: true }, // ⭐ 추가
            replace: true
        });
    };

    // 1분 타이머
    useEffect(() => {
        if (!running) return;

        const timer = setInterval(() => {
            setElapsedMs(prev => {
                const next = prev + TICK_MS;
                if (next >= DURATION_MS) {
                    clearInterval(timer);
                    finishAndReturn();
                    return DURATION_MS;
                }
                return next;
            });
        }, TICK_MS);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [running]);

    const percent = Math.min(100, Math.round((elapsedMs / DURATION_MS) * 100));
    const remainSec = Math.max(0, Math.ceil((DURATION_MS - elapsedMs) / 1000));

    // 중도 종료(홈)
    const onExit = async () => {
        setRunning(false);
        try {
            if (musicRef.current) musicRef.current.pause();
            if (videoRef.current) videoRef.current.pause();
        } catch {}
        await stopReliefLight();

        nav(REPORT_ROUTE, {
            state: { ...state, reliefDone: true }, // ⭐ 추가
            replace: true
        });
    };

    return (
        <div
            className="stress_re"
            style={{
                width: "100vw",
                height: "100vh",
                background: "#000",
                position: "relative",
                overflow: "hidden"
            }}
        >
            {/* 배경 영상 */}
            <video ref={videoRef} src={videoSrc} playsInline loop muted style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.975 }} />

            {/* 음악 */}
            <audio ref={musicRef} src={calmMusic} loop />

            {/* ✅ 페이드 전환 오버레이 */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background: "#000",
                    opacity: fadeOut ? 1 : 0,
                    transition: "opacity 450ms ease",
                    pointerEvents: "none"
                }}
            />

            {/* 상단 진행바 */}
            <div
                style={{
                    position: "absolute",
                    top: 18,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "min(720px, calc(100vw - 40px))",
                    padding: "10px 12px",
                    borderRadius: 16,
                    background: "rgba(0,0,0,0.35)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    backdropFilter: "blur(10px)",
                    color: "#fff",
                    zIndex: 3
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, opacity: 0.9 }}>
                    <div>{t("스트레스 완화 진행")}</div>
                    <div>{t("{{sec}}초", { sec: remainSec })}</div>
                </div>

                <div style={{ marginTop: 8, height: 10, borderRadius: 999, background: "rgba(255,255,255,0.14)", overflow: "hidden" }}>
                    <div
                        style={{
                            width: `${percent}%`,
                            height: "100%",
                            borderRadius: 999,
                            background: "linear-gradient(90deg, rgba(0,200,180,0.9), rgba(0,140,255,0.9))",
                            transition: "width 120ms linear"
                        }}
                    />
                </div>
            </div>

            {/* 중앙 오버레이 */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    color: "#fff",
                    zIndex: 2
                }}
            >
                {/* 호흡 원 */}
                <div
                    className="stress_one"
                    style={{
                        width: 220,
                        height: 220,
                        borderRadius: 999,
                        border: "2px solid rgba(255,255,255,0.6)",
                        boxShadow: "0 0 30px rgba(255,255,255,0.15)",
                        transform: `scale(${phase.scale})`,
                        transition: `transform ${phase.ms}ms linear`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backdropFilter: "blur(4px)",
                        background: "rgba(255,255,255,0.25)"
                    }}
                >
                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 28, fontWeight: 700 }}>{t(phase.labelKey)}</div>
                        <div style={{ marginTop: 8, fontSize: 14, opacity: 0.8 }}>{t("{{sec}}초", { sec: Math.round(phase.ms / 1000) })}</div>
                    </div>
                </div>

                <div style={{ marginTop: 56, fontSize: 14, opacity: 0.75 }}>{t("4초 들이마시고 · 2초 멈추고 · 6초 내쉬고 · 2초 멈추기")}</div>

                {/* 컨트롤 */}
                <div className="relief_controls" style={{ marginTop: 22, display: "flex", gap: 10 }}>
                    <button
                        className="relief_btn primary"
                        onClick={playMedia}
                        style={{
                            padding: "12px 16px",
                            borderRadius: 14,
                            border: "1px solid rgba(255,255,255,0.18)",
                            background: "rgba(255,255,255,0.12)",
                            color: "#fff",
                            fontWeight: 700,
                            backdropFilter: "blur(10px)",
                            cursor: "pointer"
                        }}
                    >
                        {t("음악 · 영상 재시작")}
                    </button>

                    <button
                        className="relief_btn danger"
                        onClick={onExit}
                        style={{
                            padding: "12px 16px",
                            borderRadius: 14,
                            border: "1px solid rgba(255,255,255,0.18)",
                            background: "rgba(255,70,70,0.18)",
                            color: "#fff",
                            fontWeight: 700,
                            backdropFilter: "blur(10px)",
                            cursor: "pointer"
                        }}
                    >
                        {t("종료")}
                    </button>
                </div>
            </div>

            {/* ✅ 완료 토스트 */}
            <div
                style={{
                    position: "absolute",
                    left: "50%",
                    bottom: 28,
                    transform: "translateX(-50%)",
                    padding: "12px 16px",
                    borderRadius: 999,
                    background: "rgba(0,0,0,0.55)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    color: "#fff",
                    fontWeight: 800,
                    letterSpacing: "0.2px",
                    opacity: showDoneToast ? 1 : 0,
                    transition: "opacity 200ms ease",
                    backdropFilter: "blur(10px)",
                    zIndex: 4,
                    pointerEvents: "none"
                }}
            >
                ✅ {t("완화 완료")}
            </div>
        </div>
    );
}
